/**
 * 事件属性编辑器 Schema
 *
 * 定义各事件 Code 的字段列表、控件类型与下拉选项，
 * 并提供自动推断未注册事件字段的能力。
 */
import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import { CmpObjectType, IFType, PlateType } from '@/model/EventClass/EventIF'
import { VarType } from '@/model/EventClass/EventVar'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'

/** 事件字段可用的输入控件类型。 */
export type EventFieldControl =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select'
  | 'resource-path'
  | 'string-list'
  | 'story-lock-goods'

/** 下拉/单选选项。 */
export type EventFieldOption = {
  label: string
  value: string | number | boolean
}

/** 单个事件属性的编辑描述。 */
export type EventFieldSchema = {
  key: string
  label: string
  control?: EventFieldControl
  resourceCategory?: ResourceCategoryKey
  options?: EventFieldOption[]
  placeholder?: string
  min?: number
  max?: number
  step?: number
  rows?: number
  visibleWhen?: (event: EventBase) => boolean
}

/** 某事件 Code 的完整字段 Schema。 */
type EventSchema = {
  fields: EventFieldSchema[]
}

/** IF 条件比较关系运算符（==、>=、<= 等）。 */
const RELATION_OPTIONS: EventFieldOption[] = [
  { label: '==', value: 0 },
  { label: '>=', value: 1 },
  { label: '<=', value: 2 },
  { label: '>', value: 3 },
  { label: '<', value: 4 },
  { label: '!=', value: 5 },
]

/** 数值变量操作的操作数类型（常量/随机/二周目等）。 */
const VAR_TYPE_OPTIONS: EventFieldOption[] = [
  { label: '常量', value: VarType.Constant },
  { label: '其他数值', value: VarType.Other },
  { label: '随机数', value: VarType.Random },
  { label: '二周目变量', value: VarType.VarEx },
  { label: '索引变量', value: VarType.Index },
  { label: '服务器时间', value: VarType.ServerTime },
  { label: '鲜花数', value: VarType.Flower },
  { label: '最大值', value: VarType.Max },
  { label: '最小值', value: VarType.Min },
  { label: '任务', value: VarType.Task },
  { label: '本地时间', value: VarType.LocalTime },
]

/** IF 条件分歧的类型选项。 */
const IF_TYPE_OPTIONS: EventFieldOption[] = [
  { label: '数值', value: IFType.VarIndex },
  { label: '鲜花', value: IFType.Flower },
  { label: '二周目', value: IFType.VarEx },
  { label: '鼠标', value: IFType.Mouse },
  { label: '平台', value: IFType.Platform },
  { label: '任务', value: IFType.Task },
  { label: '支付', value: IFType.Pay },
  { label: '剧情锁', value: IFType.Mall },
  { label: '限时商城', value: IFType.LimiteMall },
]

/** IF 条件比较对象类型（常量/其他数值/二周目）。 */
const CMP_OBJECT_OPTIONS: EventFieldOption[] = [
  { label: '常量', value: CmpObjectType.Constant },
  { label: '其他数值', value: CmpObjectType.Other },
  { label: '二周目变量', value: CmpObjectType.VarEx },
]

/** 天气效果索引选项。 */
const WEATHER_OPTIONS: EventFieldOption[] = [
  { label: '无天气效果', value: 0 },
  { label: '刮风', value: 1 },
  { label: '下雨', value: 2 },
  { label: '下雪', value: 3 },
]

/** 运行平台类型选项。 */
const PLATFORM_OPTIONS: EventFieldOption[] = [
  { label: 'PC', value: PlateType.PC },
  { label: 'Web', value: PlateType.WEB },
  { label: 'Android', value: PlateType.ANDROID },
  { label: 'iOS', value: PlateType.IOS },
  { label: 'H5', value: PlateType.H5 },
]

/** 变量赋值运算符（=、+=、-= 等）。 */
const OPERATOR_OPTIONS: EventFieldOption[] = [
  { label: '=', value: 0 },
  { label: '+=', value: 1 },
  { label: '-=', value: 2 },
  { label: '*=', value: 3 },
  { label: '/=', value: 4 },
  { label: '%=', value: 5 },
]

/** 通用是/否（0/1）选项。 */
const YES_NO_OPTIONS: EventFieldOption[] = [
  { label: '否', value: 0 },
  { label: '是', value: 1 },
]

/** 动画/音频时间单位（帧/秒）。 */
const TIME_UNIT_OPTIONS: EventFieldOption[] = [
  { label: '帧', value: 0 },
  { label: '秒', value: 1 },
]

/** 按属性名推断为 boolean 控件的字段名集合。 */
const BOOLEAN_NAME_HINTS = new Set([
  'AutoPlay',
  'QuickPlay',
  'ShowDialog',
  'IsFadeOut',
  'IsRead',
  'IsDelete',
  'IsOpen',
  'Switch',
  'Clear',
  'IsMirror',
  'IsFade',
  'IsPublicwebdisk',
  'IsClickSkip',
  'IsEnding',
  'IsOutBGM',
  'IsResuption',
  'IsHaveElse',
  'IsPressedDown',
  'IsIndex',
  'IsResultMinus',
  'StayOrDisapear',
])

/** 自动推断字段时应忽略的内部/元数据属性名。 */
const IGNORED_KEYS = new Set([
  'Code',
  'Parameter',
  'Indent',
  'EventLength',
  'isFold',
  'SeniorFold',
  'FoldIndent',
  'mapId',
  'eventClassName',
  'showFormat',
  'tempLang',
  'curKeyIndex',
  'localesParams',
  'ShowIndex',
  'VarIndexStr',
  'RectInfo',
])

/** 已注册事件 Code → 字段 Schema 映射表。 */
const EVENT_SCHEMAS: Partial<Record<number, EventSchema>> = {
  [EventID.AutoPlay]: {
    fields: [
      { key: 'AutoPlay', label: '自动播放', control: 'boolean' },
    ],
  },
  [EventID.QuickPlay]: {
    fields: [
      { key: 'QuickPlay', label: '快进', control: 'boolean' },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.ValueInput]: {
    fields: [
      { key: 'Value', label: '默认值', control: 'number' },
      { key: 'ValueName', label: '变量名称' },
      { key: 'Content', label: '提示内容', control: 'textarea', rows: 4 },
    ],
  },
  [EventID.PromptMessage]: {
    fields: [{ key: 'Content', label: '提示内容', control: 'textarea', rows: 4 }],
  },
  [EventID.Comment]: {
    fields: [{ key: 'Content', label: '注释内容', control: 'textarea', rows: 5 }],
  },
  [EventID.TextChoice]: {
    fields: [
      { key: 'ChoicePos', label: '选项位置', control: 'number', min: 0 },
      { key: 'Content', label: '选项内容', control: 'textarea', rows: 4 },
    ],
  },
  [EventID.OpenUrl]: {
    fields: [{ key: 'Url', label: '网页地址' }],
  },
  [EventID.ReturnGameUI]: {
    fields: [
      { key: 'isFadeOut', label: '淡出界面', control: 'boolean' },
      { key: 'Time', label: '时间', control: 'number', min: 0 },
      { key: 'type', label: '类型', control: 'number', min: 0 },
    ],
  },
  [EventID.JumpStory]: {
    fields: [
      { key: 'MapId', label: '目标剧情ID', control: 'number', min: 1 },
      { key: 'IsFadeOut', label: '淡出', control: 'boolean' },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.BackTitle]: {
    fields: [
      { key: 'IsOutBGM', label: '淡出', control: 'boolean' },
      { key: 'PicFrame', label: '帧数', control: 'number', min: 0, step: 1 },
    ],
  },
  [EventID.Wait]: {
    fields: [
      { key: 'Time', label: '等待时间', control: 'number', min: 0 },
      { key: 'Type', label: '时间类型', control: 'number', min: 0 },
    ],
  },
  [EventID.VarsEx]: {
    fields: [
      { key: 'Index', label: '变量编号', control: 'number', min: 0 },
      { key: 'Operator', label: '操作符', control: 'select', options: OPERATOR_OPTIONS },
      { key: 'Type', label: '操作数类型', control: 'select', options: VAR_TYPE_OPTIONS },
      { key: 'Value', label: '值' },
      { key: 'IsIndex', label: '按索引取值', control: 'boolean' },
      { key: 'IsResultMinus', label: '失败返回 -1', control: 'boolean' },
      { key: 'Description', label: '说明' },
      { key: 'valueUnitInfo', label: 'valueUnitInfo' },
      { key: 'keyUnitInfo', label: 'keyUnitInfo' },
    ],
  },
  [EventID.String]: {
    fields: [
      { key: 'Index', label: '字符串编号', control: 'number', min: 0 },
      { key: 'Content', label: '字符串内容', control: 'textarea', rows: 4 },
      { key: 'IsVarIndex', label: '按变量索引', control: 'boolean' },
      { key: 'keyUnitInfo', label: 'keyUnitInfo' },
    ],
  },
  [EventID.SeniorVar]: {
    fields: [
      { key: 'Index', label: '目标变量' },
      { key: 'Operator', label: '操作符1', control: 'select', options: OPERATOR_OPTIONS },
      { key: 'Type', label: '操作数类型1', control: 'select', options: VAR_TYPE_OPTIONS },
      { key: 'Value', label: '值1' },
      { key: 'Operator2', label: '操作符2', control: 'select', options: OPERATOR_OPTIONS },
      { key: 'Type2', label: '操作数类型2', control: 'select', options: VAR_TYPE_OPTIONS },
      { key: 'Value2', label: '值2' },
      { key: 'IsIndex', label: '按索引取值', control: 'boolean' },
      { key: 'IsResultMinus', label: '失败返回 -1', control: 'boolean' },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.CallSubStory]: {
    fields: [
      { key: 'MapId', label: '子剧情ID', control: 'number', min: 1 },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.StoryLock]: {
    fields: [
      { key: 'CommodityId', label: '商品', control: 'story-lock-goods' },
    ],
  },
  [EventID.Weather]: {
    fields: [
      { key: 'WeatherIndex', label: '天气', control: 'select', options: WEATHER_OPTIONS },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.Shake]: {
    fields: [
      { key: 'Intensity', label: '强度', control: 'number' },
      { key: 'Speed', label: '速度', control: 'number' },
      { key: 'IsFor', label: '循环', control: 'boolean' },
      { key: 'ShakeTime', label: '持续时间', control: 'number' },
      { key: 'Description', label: '说明' },
      { key: 'Type', label: '类型', control: 'number' },
    ],
  },
  [EventID.Flash]: {
    fields: [
      { key: 'Color', label: '颜色' },
      { key: 'Time', label: '持续时间', control: 'number' },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.UpateSceneHue]: {
    fields: [
      { key: 'Color', label: '色调' },
      { key: 'Saturation', label: '饱和度', control: 'number' },
      { key: 'Time', label: '持续时间', control: 'number' },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.BGMAdd]: {
    fields: [
      { key: 'Index', label: '鉴赏编号', control: 'number', min: 0 },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.CGAdd]: {
    fields: [
      { key: 'Index', label: '鉴赏编号', control: 'number', min: 0 },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.UnlockCollect]: {
    fields: [
      { key: 'OneIndex', label: '一级序号', control: 'number', min: 0 },
      { key: 'TwoIndex', label: '二级序号', control: 'number', min: 0 },
      { key: 'IsShare', label: '分享解锁', control: 'boolean' },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.DisposePic]: {
    fields: [
      { key: 'PicId', label: '图层', control: 'number', min: 1, max: 23 },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.ResourceLoad]: {
    fields: [{ key: 'FileList', label: '资源列表', control: 'string-list' }],
  },
  [EventID.StartSE]: {
    fields: [
      { key: 'Path', label: '路径', control: 'resource-path', resourceCategory: 'audio' },
      { key: 'Vol', label: '音量', control: 'number', min: 0, max: 100 },
      { key: 'Description', label: '说明' },
      { key: 'IsPublicwebdisk', label: '网盘资源', control: 'boolean' },
      { key: 'IsFade', label: '淡入', control: 'boolean' },
      { key: 'Time', label: '淡入时间', control: 'number', min: 0 },
      { key: 'TimeUnit', label: '时间单位', control: 'select', options: TIME_UNIT_OPTIONS },
    ],
  },
  [EventID.StartVoice]: {
    fields: [
      { key: 'Path', label: '路径', control: 'resource-path', resourceCategory: 'audio' },
      { key: 'Vol', label: '音量', control: 'number', min: 0, max: 100 },
      { key: 'Description', label: '说明' },
      { key: 'IsPublicwebdisk', label: '网盘资源', control: 'boolean' },
      { key: 'IsFade', label: '淡入', control: 'boolean' },
      { key: 'Time', label: '淡入时间', control: 'number', min: 0 },
      { key: 'TimeUnit', label: '时间单位', control: 'select', options: TIME_UNIT_OPTIONS },
    ],
  },
  [EventID.StartBGS]: {
    fields: [
      { key: 'Path', label: '路径', control: 'resource-path', resourceCategory: 'audio' },
      { key: 'Vol', label: '音量', control: 'number', min: 0, max: 100 },
      { key: 'Description', label: '说明' },
      { key: 'IsPublicwebdisk', label: '网盘资源', control: 'boolean' },
      { key: 'IsFade', label: '淡入', control: 'boolean' },
      { key: 'Time', label: '淡入时间', control: 'number', min: 0 },
      { key: 'TimeUnit', label: '时间单位', control: 'select', options: TIME_UNIT_OPTIONS },
    ],
  },
  [EventID.FadeBGM]: {
    fields: [
      { key: 'Time', label: '淡出时间', control: 'number', min: 0 },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.StopSE]: {
    fields: [{ key: 'Time', label: '停止时间', control: 'number', min: 0 }],
  },
  [EventID.StopVoice]: {
    fields: [{ key: 'Time', label: '停止时间', control: 'number', min: 0 }],
  },
  [EventID.FadeBGS]: {
    fields: [
      { key: 'Time', label: '淡出时间', control: 'number', min: 0 },
      { key: 'Description', label: '说明' },
    ],
  },
  [EventID.MediaPlayNode]: {
    fields: [
      { key: 'VideoId', label: '视频图层', control: 'number', min: 1 },
      { key: 'TimePoint', label: '时间点', control: 'number', min: 0 },
      { key: 'VideoPlayType', label: '节点动作', control: 'number', min: 1 },
      { key: 'Description', label: '说明' },
      { key: 'IsSelects', label: '显示选项', control: 'boolean' },
    ],
  },
  [EventID.MediaPlayEnd]: {
    fields: [{ key: 'Description', label: '视频ID/说明' }],
  },
  [EventID.VideoShow]: {
    fields: [
      { key: 'VideoId', label: '视频图层', control: 'number', min: 1 },
      { key: 'VideoPath', label: '视频路径', control: 'resource-path', resourceCategory: 'video' },
      { key: 'X', label: 'X', control: 'number', step: 1 },
      { key: 'Y', label: 'Y', control: 'number', step: 1 },
      { key: 'ZoomX', label: '缩放X', control: 'number', min: 0, max: 1000, step: 1 },
      { key: 'ZoomY', label: '缩放Y', control: 'number', min: 0, max: 1000, step: 1 },
      { key: 'Volume', label: '音量', control: 'number', min: 0, max: 100, step: 1 },
      { key: 'IsLoop', label: '循环播放', control: 'boolean' },
      { key: 'Description', label: '说明' },
      { key: 'IsPublicwebdisk', label: '网盘资源', control: 'boolean' },
      { key: 'IsUserStrAssigned', label: '字符串指定路径', control: 'boolean' },
      { key: 'UserAssignedStrID', label: '字符串编号', control: 'number', min: 0 },
      { key: 'IsClickSkip', label: '点击跳过', control: 'boolean' },
    ],
  },
  [EventID.BookMark]: {
    fields: [
      { key: 'MarkID', label: '书签ID', control: 'number', min: 0 },
      { key: 'Content', label: '书签内容' },
      { key: 'Description', label: '说明' },
    ],
  },
}

/** 根据属性名与当前值推断合适的控件类型。 */
function inferControl(key: string, value: unknown): EventFieldControl {
  if (Array.isArray(value)) return 'string-list'
  if (typeof value === 'boolean') return 'boolean'
  if (typeof value === 'number') return 'number'
  if (
    typeof value === 'string' &&
    /(Path|Portrait|Voice|BorderPath|DialogPath|FacePath|VideoPath|PicPath)$/i.test(key)
  ) {
    return 'resource-path'
  }
  if (
    typeof value === 'string' &&
    /(Content|Comment|Subtitle|Message|Text|FileList)$/i.test(key)
  ) {
    return 'textarea'
  }
  if (BOOLEAN_NAME_HINTS.has(key) || /^Is[A-Z]/.test(key)) return 'boolean'
  return 'text'
}

/** 将 camelCase 属性名格式化为可读标签。 */
function prettifyLabel(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
}

/** 按常见属性名推断 number 控件的 min/max/step。 */
function inferNumberConstraints(key: string): Pick<EventFieldSchema, 'min' | 'max' | 'step'> {
  if (key === 'Opacity' || /Opacity/i.test(key)) {
    return { min: 0, max: 255, step: 1 }
  }
  if (key === 'Volume' || /Volume/i.test(key)) {
    return { min: 0, max: 100, step: 1 }
  }
  if (/^(ZoomX|ZoomY)$/i.test(key) || /Zoom/i.test(key)) {
    return { min: 0, max: 1000, step: 1 }
  }
  if (/Angle/i.test(key)) {
    return { min: -360, max: 360, step: 1 }
  }
  if (/(Width|Height)$/i.test(key)) {
    return { min: 0, step: 1 }
  }
  if (/^(X|Y)$/i.test(key) || /(Pos|Point|Offset)[XY]?$/i.test(key)) {
    return { step: 1 }
  }
  return { step: 1 }
}

/** 为未注册 Schema 的单个属性生成自动推断字段描述。 */
function getAutoField(event: EventBase, key: string): EventFieldSchema | null {
  let value: unknown
  try {
    value = (event as unknown as Record<string, unknown>)[key]
  } catch {
    return null
  }
  if (typeof value === 'function' || value === undefined) return null
  const control = inferControl(key, value)
  return {
    key,
    label: prettifyLabel(key),
    control,
    ...(control === 'number' ? inferNumberConstraints(key) : {}),
  }
}

/** 收集事件 class 原型链上所有可写属性名。 */
function collectPrototypeKeys(event: EventBase): string[] {
  const keys: string[] = []
  const seen = new Set<string>()
  let proto = Object.getPrototypeOf(event)
  while (proto && proto.constructor && proto.constructor.name !== 'EventBase') {
    for (const name of Object.getOwnPropertyNames(proto)) {
      if (seen.has(name) || IGNORED_KEYS.has(name) || name.startsWith('_')) continue
      const descriptor = Object.getOwnPropertyDescriptor(proto, name)
      if (!descriptor) continue
      if (typeof descriptor.get !== 'function' && typeof descriptor.set !== 'function') continue
      if (typeof descriptor.set !== 'function') continue
      seen.add(name)
      keys.push(name)
    }
    proto = Object.getPrototypeOf(proto)
  }
  return keys
}

/** 获取指定事件 Code 的预注册 Schema，未注册则返回 null。 */
export function getEventSchema(eventCode: number): EventSchema | null {
  return EVENT_SCHEMAS[eventCode] ?? null
}

/** 获取 IF 条件类型下拉选项。 */
export function getIfTypeOptions(): EventFieldOption[] {
  return IF_TYPE_OPTIONS
}

/** 获取 IF 比较关系运算符选项。 */
export function getRelationOptions(): EventFieldOption[] {
  return RELATION_OPTIONS
}

/** 获取 IF 比较对象类型选项。 */
export function getCmpObjectOptions(): EventFieldOption[] {
  return CMP_OBJECT_OPTIONS
}

/** 获取数值变量操作数类型选项。 */
export function getVarTypeOptions(): EventFieldOption[] {
  return VAR_TYPE_OPTIONS
}

/** 获取运行平台类型选项。 */
export function getPlatformOptions(): EventFieldOption[] {
  return PLATFORM_OPTIONS
}

/** 获取通用是/否选项。 */
export function getYesNoOptions(): EventFieldOption[] {
  return YES_NO_OPTIONS
}

/**
 * 解析事件应展示的编辑字段列表。
 * 优先使用预注册 Schema，否则自动推断 class 可写属性。
 */
export function resolveEventEditorFields(event: EventBase): EventFieldSchema[] {
  const schema = getEventSchema(event.Code)
  if (schema) {
    return schema.fields.filter((field) => (field.visibleWhen ? field.visibleWhen(event) : true))
  }
  return collectPrototypeKeys(event)
    .map((key) => getAutoField(event, key))
    .filter((field): field is EventFieldSchema => !!field)
}
